import axios from 'axios';
import { differenceInSeconds } from 'date-fns';
import { useEffect, useState } from 'react';
import { generateSystemName } from '../utils/create';
import { stripHtmlTags } from '../utils/general';
import { getGoogleAnalyticsClientId, gtmEventCustomizationSkipped, gtmEventCustomizedTrial, gtmEventRegisterTrial } from '../utils/google';
import { submitHubSpotForm, updateHubSpotContact } from '../utils/hubspot';
import { consumeSearchParam } from '../utils/url';
import { isInternalEmailAddress } from '../utils/validate';
import { useLocation } from './useLocation';
import { useTrialPersistence } from './useTrialPersistence';

const getUserOrigin = (email, regexPatterns) => (isInternalEmailAddress(email, regexPatterns) ? 'internal' : 'external');

const requestTrialRegistration = async ({ formData, locale, recaptchaToken, region, url }) => {
	const body = {
		...formData,
		reCaptchaCode: recaptchaToken,
		locale,
		region: region.toUpperCase(),
		systemName: generateSystemName(),
	};

	if (getGoogleAnalyticsClientId()) {
		body.gaClientId = getGoogleAnalyticsClientId();
	}

	try {
		const { data } = await axios.post(url, body);
		return data;
	} catch (error) {
		const status = error.response?.status;
		const messages = {
			400: 'Validation error on provided fields',
			409: 'Selected system name already exists',
			500: 'Request failed with an unexpected reason',
		};
		throw new Error(messages[status] || `Unknown status code exception: ${status}`);
	}
};

const requestTrialActivation = async ({ activationCode, customJson, region, systemId, templateName, url }) => {
	try {
		const { data } = await axios.post(url, {
			activationCode,
			customJson,
			region: region.toUpperCase(),
			systemId,
			templateName,
		});
		return data;
	} catch (error) {
		const status = error.response?.status;
		const messages = {
			400: 'Validation error on provided fields',
			403: 'Provided activation code was invalid for the supplied system',
			404: 'No system with the id was found',
			422: 'No template with the name was found',
			500: 'Request failed with an unexpected reason',
		};
		throw new Error(messages[status] || `Unknown status code exception: ${status}`);
	}
};

const requestActivationEmailResend = async ({ region, systemId, url }) => {
	try {
		await axios.post(url, {
			region: region.toUpperCase(),
			systemId,
		});
	} catch (error) {
		const status = error.response?.status;
		const messages = {
			400: 'Validation error on provided fields',
			500: 'Request failed with an unexpected reason',
		};
		throw new Error(messages[status] || `Unknown status code exception: ${status}`);
	}
};

export const useTrial = ({ data, getLabel, getRecaptchaToken, isMobile, onStatusChange, rememberTrialSystem }) => {
	const { country, region } = useLocation(data?.apiRoutes.region);
	const { getPersistedValue, persistState, clearPersistedState } = useTrialPersistence();

	// Consume search params once
	const initialActivationCode = consumeSearchParam('activationCode') || '';
	const initialEmail = consumeSearchParam('email') || '';
	const initialSystemId = consumeSearchParam('systemId') || '';
	const initialTrialRegistrationMethod = consumeSearchParam('sso') || 'email';

	// Initialize state with search params prioritized over persisted state
	const [trialActivationCode, setTrialActivationCode] = useState(initialActivationCode || getPersistedValue('trialActivationCode') || '');
	const [trialEmailAddress, setTrialEmailAddress] = useState(initialEmail || getPersistedValue('trialEmailAddress') || '');
	const [trialLoading, setTrialLoading] = useState(false);
	const [trialSystemId, setTrialSystemId] = useState(initialSystemId || getPersistedValue('trialSystemId') || '');
	const [trialSystemUrl, setTrialSystemUrl] = useState('');
	const [trialTrusted, setTrialTrusted] = useState(!!initialActivationCode || getPersistedValue('trialTrusted') || false);
	const [trialRegistrationMethod] = useState(initialTrialRegistrationMethod);
	const [hasRegisteredSSO, setHasRegisteredSSO] = useState(false);
	const minLoadingTime = 3 * 1000; // Minimum loading time in milliseconds

	// Persist state changes to sessionStorage
	useEffect(() => {
		persistState({
			trialActivationCode,
			trialEmailAddress,
			trialSystemId,
			trialTrusted,
		});
	}, [trialActivationCode, trialEmailAddress, trialSystemId, trialTrusted, persistState]);

	// Clear persisted state (useful for reload)
	const clearTrialState = () => clearPersistedState();

	useEffect(() => {
		if (trialSystemId) {
			if (trialActivationCode) {
				if (trialSystemUrl) {
					onStatusChange('activated');
				} else {
					onStatusChange('customization');
				}
			} else {
				onStatusChange('registered');
			}
		}
	}, [trialSystemId, trialActivationCode, trialSystemUrl]);

	const sendRegisterTrialEvent = (loadStartDate = null, eventData = {}) => {
		gtmEventRegisterTrial({
			trusted: eventData.trusted ?? trialTrusted,
			userOrigin: getUserOrigin(eventData.email ?? trialEmailAddress, data.internalEmailRegexPatterns),
			region: region,
			systemId: eventData.systemId ?? trialSystemId,
			registrationMethod: trialRegistrationMethod,
			...(loadStartDate ? { timeToLoad: differenceInSeconds(new Date(), loadStartDate) } : {}),
		});
	};

	const doHubSpotFormSubmission = async (formData) => {
		await submitHubSpotForm({
			consentText: stripHtmlTags(getLabel('registration_consent_text')),
			country,
			formData,
			hubspotData: data.hubspot,
			region,
			trialRegistrationMethod,
			url: data.apiRoutes?.hubspot.submitForm,
		});
	};

	// Send GTM event and data to HubSpot when the user has registered for a trial via SSO
	useEffect(() => {
		if (hasRegisteredSSO || !data || trialRegistrationMethod === 'email') {
			return;
		}
		setHasRegisteredSSO(true);

		(async () => {
			await doHubSpotFormSubmission([{ name: 'email', value: trialEmailAddress }]);
			sendRegisterTrialEvent();
		})();
	}, [data]);

	const onRegistrationFormSubmit = async ({ getFormData, getHubspotFormData }) => {
		setTrialLoading(true);
		const formData = getFormData();
		setTrialEmailAddress(formData.email);

		const loadingStartTime = Date.now();

		try {
			const recaptchaToken = isMobile ? '' : await getRecaptchaToken();
			const { status, systemId, activationCode } = await requestTrialRegistration({
				formData,
				locale: data?.locale,
				recaptchaToken,
				region,
				url: data?.apiRoutes.start,
			});

			await doHubSpotFormSubmission(getHubspotFormData());
			sendRegisterTrialEvent(loadingStartTime, {
				trusted: status === 'TRUSTED',
				email: formData.email,
				systemId: systemId,
			});

			setTrialSystemId(systemId);
			setTrialTrusted(status === 'TRUSTED');
			setTrialActivationCode(activationCode);
		} catch (error) {
			console.error('Error registering trial:', error);
			// TODO: Handle error appropriately, e.g., show a notification to the user
		} finally {
			const elapsedTime = Date.now() - loadingStartTime;
			if (elapsedTime < minLoadingTime) {
				setTimeout(() => setTrialLoading(false), minLoadingTime - elapsedTime);
			} else {
				setTrialLoading(false);
			}
		}
	};

	const onCompleteCustomization = async ({ selection, trialTemplate, skipped }) => {
		try {
			const { redirectUrl, email } = await requestTrialActivation({
				activationCode: trialActivationCode,
				customJson: skipped ? {} : { trialUserGoal: selection[0].value, trialUserRole: selection[1].value },
				region,
				systemId: trialSystemId,
				templateName: trialTemplate,
				url: data.apiRoutes.activate,
			});

			setTrialEmailAddress(email);
			setTrialSystemUrl(redirectUrl);
			rememberTrialSystem(trialSystemId);

			// Update HubSpot contact
			await updateHubSpotContact({
				url: data.apiRoutes.hubspot.updateContact,
				email,
				properties: {
					trial_template: trialTemplate,
					...(!skipped && { trial_goal: selection[0].label, trial_role: selection[1].label }),
				},
			});

			// Send GTM event for trial customization
			skipped ? gtmEventCustomizationSkipped(trialTemplate) : gtmEventCustomizedTrial({ selection, trialTemplate });

			// Redirect the user to the trial system URL
			window.location.assign(redirectUrl);
		} catch (error) {
			console.error('An error occurred while trying to activate the trial:', error);
			// TODO: Handle error appropriately, e.g., show a notification to the user
		}
	};

	const resendActivationEmail = async () => {
		try {
			await requestActivationEmailResend({
				region,
				systemId: trialSystemId,
				url: data.apiRoutes.resendActivateMail,
			});

			// Show a toast or notification to inform the user that the email has been resent
			window.showToast?.(getLabel('verification_toast_new_email_success'), 'success');
		} catch (error) {
			console.error('An error occurred while trying to resend the activation email:', error);
			// Show a toast or notification to inform the user that there was an error
			window.showToast?.(getLabel('verification_toast_new_email_error'), 'error', { autoHide: false });
		}
	};

	return {
		clearTrialState,
		onCompleteCustomization,
		onRegistrationFormSubmit,
		resendActivationEmail,
		trialActivationCode,
		trialEmailAddress,
		trialLoading,
		trialSystemId,
	};
};
